import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  Divider,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { FunctionComponent, RefObject, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import Card from '../../../common/components/Card'
import { useFeatureToggle } from '../../../common/utils/feature-toggle/useFeatureToggle'
import AlertModel from '../../../components/AlertModel'
import AuthenticationModal from '../../../components/AuthenticationModal'
import ResponsiveStack from '../../../components/shared-components/ResponsiveStack'
import {
  useCreatePrescriptionMutation,
  CreatePrescriptionResponse,
  DrugItem,
  CreatePrescriptionRequest,
} from '../../../services/prescriptions'
import { Patient, PrescribedItem, Prescription } from '../../../types'
import GoogleAnalyticsTagManager from '../../../utils/google-analytics/GoogleAnalyticsTagManager'
import { useGAContextData } from '../../../utils/google-analytics/useGAContextData'
import { isValidEmailFormat } from '../../../utils/validation-helpers/emailFormatValidate'
import { isMobileNumberInvalid } from '../../../utils/validation-helpers/mobileNumberFormatValidate'
import {
  definedPrescribedItemsSelector,
  setDeliveryState,
  setIsPaperPrescription,
  setScriptState,
  uncheckIsPaperPrescription,
} from '../ScriptForm/PrescribedItem/PrescribedItemSlice'
import { isSchedule8Medicine } from '../utilities'
import ReviewPrescriptionModal from './PreviewPrescriptionModal'
import { PrescriptionDeliveryMethod } from '../../../types/prescription-delivery-method'
import ClientSSOContext from '../../../features/sso/types/ClientSSOContext'
import SSOAuthenticationModal from '../../../components/SSOAuthenticationModal'
import SendScriptResultsModal from './SendScriptResultsModal'
import SendScriptOverallCheckModal, { ReauthenticationInfo } from './SendScriptOverallCheckModal'
import { useBatchMutation, SettledResult } from '../../../hooks/useBatchMutation'
import { transformPrescriptions } from '../../PrescriptionsPage/utilities'
import { ReIssuePrescriptionModal } from '../../PrescriptionsPage/components/ReIssuePrescriptionModal'

interface SendScriptProps {
  disabledSubmit: boolean
  isFormValid: () => boolean
  isActive: boolean
  prescribedItemListRef?: RefObject<HTMLDivElement | null>
}

const dateToLocalDate = (date: Date = new Date()) => {
  const offset = date.getTimezoneOffset()
  date = new Date(date.getTime() - offset * 60 * 1000)
  return date.toISOString().split('T')[0]
}

const SendScriptPanel: FunctionComponent<SendScriptProps> = ({
  disabledSubmit,
  isFormValid,
  isActive,
  prescribedItemListRef,
}) => {
  const toast = useToast()
  const dispatch = useAppDispatch()
  const { isFeatureEnabled: isOrgRegistrationAndPaymentFeatureOn } = useFeatureToggle(
    'ORG_REGISTRATION_AND_PAYMENT'
  )
  const {
    sendViaEmail,
    sendViaSMS,
    printPaperToken,
    recipientEmail,
    recipientMobile,
    isPaperPrescription,
  } = useAppSelector((state) => state.newScript)

  const isTheDeliveryMethodValid = () => {
    if (printPaperToken || isPaperPrescription) {
      return true
    }
    if (sendViaEmail) {
      return isValidEmailFormat(recipientEmail)
    }
    if (sendViaSMS) {
      return !isMobileNumberInvalid(recipientMobile)
    }
    return false
  }
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)

  const { email: patientEmailDefault, phone_number: patientMobileDefault }: Patient =
    selectedPatient

  const {
    isOpen: isReviewPrescriptionModalOpen,
    onOpen: onOpenReviewPrescriptionModal,
    onClose: onCloseReviewPrescriptionModal,
  } = useDisclosure()
  const [isAlertModelOpen, setIsAlertModelOpen] = useState<boolean>(false)
  const [isPaperPrescriptionConfirmationModalOpen, setIsPaperPrescriptionConfirmationModalOpen] =
    useState<boolean>(false)

  const [isResendModelOpen, setIsResendModelOpen] = useState<boolean>(false)

  const {
    onClose: onCloseStepperModal,
    isOpen: isStepperModalOpen,
    onOpen: onOpenStepperModal,
  } = useDisclosure()

  const {
    onClose: onCloseScriptResultModal,
    isOpen: isScriptResultModalOpen,
    onOpen: onOpenScriptResultModal,
  } = useDisclosure()

  const [reauthenticationInfo, setReauthenticationInfo] = useState<ReauthenticationInfo>({
    isAuthModalOpen: false,
    isSSOAuthModalOpen: false,
    isDone: false,
    ssoError: undefined,
  })

  const [prescriptionsNumber, setPrescriptionsNumber] = useState<{
    success: number
    failed: number
  }>({
    success: 0,
    failed: 0,
  })
  const [validPrescriptions, setValidPrescriptions] = useState<Prescription[]>([])

  const GAContextData = useGAContextData()

  const organization = useAppSelector((state) => state.organization?.data)
  const organizationSettings = organization?.settings
  const defaultPrescriptionDeliveryMethod = organizationSettings?.defaultPrescriptionDeliveryMethod
  const isEnableS8Medicine = organizationSettings?.enableS8Medicine
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const workLocationId = useAppSelector((state) => state.prescriber.workLocationId)
  const prescribedItems = useAppSelector(definedPrescribedItemsSelector)

  const [isEmergencySupply, setIsEmergencySupply] = useState<boolean>(false)
  const [isSendToPharmacy, setIsSendToPharmacy] = useState<boolean>(false)

  const [createPrescription] = useCreatePrescriptionMutation()

  const {
    abort: abortCreatePrescriptionBatch,
    getAllStates,
    triggerSettled,
    reset: resetCreatePrescriptionBatch,
    isLoading: isCreatePrescriptionBatchLoading,
  } = useBatchMutation<CreatePrescriptionRequest, CreatePrescriptionResponse>(createPrescription)

  const clientSSOContextData: ClientSSOContext | null = useMemo(() => {
    try {
      const clientSSOContextDataString = localStorage.getItem('clientSSOContext')
      return clientSSOContextDataString ? JSON.parse(clientSSOContextDataString) : null
    } catch (error) {
      console.error('Failed to parse client SSO context:', error)
      return null
    }
  }, [])

  const shouldUseSSOAuthentication = () => {
    return clientSSOContextData && clientSSOContextData.clientName
  }

  const availableResendPrescriptions = useMemo(() => {
    if (validPrescriptions) {
      const results = validPrescriptions.filter((prescription) => {
        const { isPaperPrescription, isEmergencySupply, isSendToPharmacy } =
          transformPrescriptions(prescription)

        return !(isPaperPrescription || isEmergencySupply || isSendToPharmacy)
      })
      return results
    }
    return []
  }, [validPrescriptions])

  const resetPrescriptionsBatch = () => {
    resetCreatePrescriptionBatch()
    setPrescriptionsNumber({
      success: 0,
      failed: 0,
    })
    setValidPrescriptions([])
  }

  const onSubmitPrescriptions = async (
    scids: string[],
    guids: string[],
    latestPrescribedItems?: PrescribedItem[]
  ) => {
    const itemsToSubmit = latestPrescribedItems ?? prescribedItems

    if (
      !scids?.length ||
      !prescriber ||
      !selectedPatient ||
      !guids?.length ||
      !itemsToSubmit?.length
    ) {
      return
    }

    resetPrescriptionsBatch()
    onOpenScriptResultModal()

    let timeoutId: NodeJS.Timeout | null = null
    if (organizationSettings?.eRxTimeout) {
      timeoutId = setTimeout(() => {
        abortCreatePrescriptionBatch()
        setIsAlertModelOpen(true)
      }, (organizationSettings?.eRxTimeout || 60) * 1000)
    }

    const createPrescriptionPromises: Array<Promise<SettledResult<CreatePrescriptionResponse>>> = []

    itemsToSubmit.forEach((drug, index) => {
      const key = index.toString()
      createPrescriptionPromises.push(
        triggerSettled(`${key}-${drug.productId}`, {
          newPrescription: {
            scid: scids[index],
            guid: guids[index],
            isPaperPrescription,
            prescriberId: prescriber.id,
            patientId: selectedPatient.id,
            sendViaEmail,
            sendViaSMS,
            printPaperToken,
            recipientEmail,
            recipientMobile,
            metadata: clientSSOContextData?.metadata,
            referralId: clientSSOContextData?.referralId,
            externalPatientId: clientSSOContextData?.externalPatientFetchId,
            locationId: workLocationId,
            items: [
              {
                ...drug,
                productId: drug.productId,
                routeAdministration: drug.routeAdministration,
                numberOfRepeatsAuthorised: drug.numberOfRepeatsAuthorised,
                doctorNotes: drug.doctorsNotes,
                scriptCTGAnnotation: drug.ctgAnnotation,
                prescribedItemReserved01: drug.quantity?.toString() || '0',
                repeatIntervals: drug.repeatIntervals ?? 0,
                emergencySupply: drug.emergencySupply,
                snomedctReasonForPrescribe: 0,
                sendToPharmacy: drug.sendToPharmacy,
                prescribedItemReserved04: drug.prescribedItemReserved04,
                prescriberScriptNumber: drug.prescriberScriptNumber,
              },
            ] as DrugItem[],
          },
          config: {
            headers: isOrgRegistrationAndPaymentFeatureOn
              ? {
                  'billing-feature-toggle': 'enable',
                }
              : undefined,
          },
        })
      )
    })

    const responses = await Promise.all(createPrescriptionPromises)

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    const validResponses = responses.filter(
      (response) =>
        response.success &&
        response.data?.eRxPrescription?.status === 'Success' &&
        !!response.data?.prescription?.scid
    )

    const failedResponses = responses.filter((response) => !validResponses.includes(response))
    setPrescriptionsNumber({
      success: validResponses.length,
      failed: failedResponses.length,
    })
    setValidPrescriptions(
      validResponses
        .map((response) => response.data?.prescription)
        .filter((prescription): prescription is Prescription => !!prescription)
    )
    if (failedResponses.length > 0) {
      GoogleAnalyticsTagManager.sendPrescriptionCreationErrorEvent({
        ...GAContextData,
        error: `Failed to create ${failedResponses.length} prescription(s) in server`,
      })
    }
  }

  const checkIfSelectedDrugIncludesS8 = () =>
    prescribedItems.some((drug) => isSchedule8Medicine(drug))

  useEffect(() => {
    // Check if any drug requires emergency supply
    setIsEmergencySupply(prescribedItems.some((drug) => drug.emergencySupply))

    // Check if any drug is being sent to pharmacy
    setIsSendToPharmacy(prescribedItems.some((drug) => !!drug.sendToPharmacy))
  }, [prescribedItems])

  useEffect(() => {
    if (isEmergencySupply || isSendToPharmacy) {
      dispatch(
        setDeliveryState({
          recipientEmail: '',
          recipientMobile: '',
        })
      )
    } else {
      // Initialize delivery state based on organization settings
      const deliveryState: {
        recipientEmail: string
        recipientMobile: string
        sendViaEmail?: boolean
        sendViaSMS?: boolean
        printPaperToken?: boolean
      } = {
        recipientEmail: patientEmailDefault,
        recipientMobile: patientMobileDefault,
      }

      // Set delivery method based on organization settings if available
      if (defaultPrescriptionDeliveryMethod) {
        deliveryState.sendViaEmail =
          defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Email
        deliveryState.sendViaSMS =
          defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.SMS
        deliveryState.printPaperToken =
          defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Paper_Token
      } else {
        // Default to Paper_Token if no organization setting
        deliveryState.printPaperToken = true
      }

      dispatch(setDeliveryState(deliveryState))
    }
  }, [
    isEmergencySupply,
    isSendToPharmacy,
    dispatch,
    patientEmailDefault,
    patientMobileDefault,
    defaultPrescriptionDeliveryMethod,
  ])

  useEffect(() => {
    dispatch(setIsPaperPrescription(prescriber?.paperPrescriptionOnly || false))
  }, [prescriber?.paperPrescriptionOnly, dispatch])

  const handleUpdateIsPaperPrescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedPatient.paper_only) {
      if (!isPaperPrescription) {
        dispatch(
          setScriptState({
            isPaperPrescription: true,
          })
        )
      }

      return
    }

    const isChecked = e.target.checked
    if (isPaperPrescription && !isChecked) {
      setIsPaperPrescriptionConfirmationModalOpen(true)
      return
    }

    dispatch(
      setScriptState({
        isPaperPrescription: isChecked,
      })
    )
  }

  const handleUpdateIsPaperPrescriptionConfirmation = () => {
    dispatch(uncheckIsPaperPrescription())
    setIsPaperPrescriptionConfirmationModalOpen(false)
  }

  const closeReIssueModal = () => {
    setIsResendModelOpen(false)
  }

  const handleCloseScriptResultModalByClose = () => {
    onCloseScriptResultModal()
    resetPrescriptionsBatch()
    setReauthenticationInfo((state) => ({
      ...state,
      isDone: false,
    }))
  }

  const handleCloseScriptResultModalByEditFailed = () => {
    onCloseScriptResultModal()
    resetPrescriptionsBatch()
  }

  const printPaperTokenOnly = isEmergencySupply || isSendToPharmacy

  return (
    <Card p="16px">
      <VStack
        alignItems="stretch"
        sx={{
          input: {
            backgroundColor: 'white',
          },
        }}
      >
        <VStack>
          <ResponsiveStack justifyContent="space-between" width="100%">
            <HStack>
              <Text fontWeight="bold">Date</Text>
              <Input
                type="date"
                disabled
                placeholder="22/07/2022"
                value={dateToLocalDate()}
              ></Input>
            </HStack>

            <Checkbox
              isDisabled={prescriber?.paperPrescriptionOnly || selectedPatient.paper_only}
              isChecked={isPaperPrescription}
              fontWeight="bold"
              onChange={handleUpdateIsPaperPrescription}
            >
              Create Paper Script
            </Checkbox>
          </ResponsiveStack>

          {selectedPatient.paper_only && (
            <Text color="red.300" fontSize="xs" alignSelf="end">
              The checkbox is not able to be changed, because the patient is paper only
            </Text>
          )}
        </VStack>

        {(isEmergencySupply || isSendToPharmacy) && (
          <VStack alignItems="flex-end" spacing="4">
            <HStack>
              <Alert status="warning" variant="solid">
                <AlertIcon />
                NO electronic token will be sent to patient. Please print EOP from prescription
                page.
              </Alert>
            </HStack>
          </VStack>
        )}

        <VStack alignItems={['flex-start', 'flex-end']} spacing="4">
          {isPaperPrescription ? null : (
            <>
              <Divider />
              <HStack>
                <RadioGroup
                  defaultValue="Paper_Token"
                  value={sendViaEmail ? 'Email' : sendViaSMS ? 'SMS' : 'Paper_Token'}
                  onChange={(value: 'Email' | 'SMS' | 'Paper_Token') =>
                    dispatch(
                      setDeliveryState({
                        sendViaEmail: value === 'Email',
                        sendViaSMS: value === 'SMS',
                        printPaperToken: value === 'Paper_Token',
                      })
                    )
                  }
                >
                  <ResponsiveStack fontWeight="bold" alignItems="flex-start">
                    <Radio value="Paper_Token">{`Print paper token only`}</Radio>
                    {printPaperTokenOnly ? null : (
                      <>
                        <Radio value="SMS">{`Send to patient mobile`}</Radio>
                        <Radio value="Email">{`Send to patient email`}</Radio>
                      </>
                    )}
                  </ResponsiveStack>
                </RadioGroup>
              </HStack>
              {sendViaEmail ? (
                <HStack>
                  <Text fontWeight="bold">Patient email</Text>
                  <Input
                    value={recipientEmail}
                    onChange={(e) =>
                      dispatch(
                        setDeliveryState({
                          recipientEmail: e.target.value,
                        })
                      )
                    }
                  ></Input>
                </HStack>
              ) : null}
              {sendViaSMS && !isEmergencySupply ? (
                <HStack>
                  <Text fontWeight="bold">Patient mobile</Text>
                  <Input
                    value={recipientMobile}
                    onChange={(e) =>
                      dispatch(
                        setDeliveryState({
                          recipientMobile: e.target.value,
                        })
                      )
                    }
                  ></Input>
                </HStack>
              ) : null}
            </>
          )}
          {reauthenticationInfo.isDone && (
            <Alert status="success" variant="subtle">
              <AlertIcon />
              User verification successful, please start submission
            </Alert>
          )}
          {reauthenticationInfo.ssoError && (
            <Alert status="error" variant="subtle">
              <AlertIcon />
              <AlertDescription>{reauthenticationInfo.ssoError}</AlertDescription>
            </Alert>
          )}
          <VStack>
            {!isActive && (
              <Alert status={'error'}>
                <AlertIcon />
                <AlertDescription fontSize="sm">Your account is deactivated</AlertDescription>
              </Alert>
            )}
            <Button
              alignSelf={['center', 'flex-end']}
              disabled={disabledSubmit || !isTheDeliveryMethodValid() || !isActive}
              mt="10"
              size="lg"
              colorScheme="blue"
              width="250px"
              onClick={() => {
                if (!isFormValid()) {
                  toast.close('new-script-form-invalid')
                  toast({
                    id: 'new-script-form-invalid',
                    title: 'Form is invalid',
                    description: 'Please check the form with errors highlighted and try again.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                    position: 'top',
                  })
                  prescribedItemListRef?.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                  })
                  return
                }
                if (
                  checkIfSelectedDrugIncludesS8() &&
                  !reauthenticationInfo.isDone &&
                  isEnableS8Medicine
                ) {
                  if (shouldUseSSOAuthentication()) {
                    setReauthenticationInfo((state) => ({ ...state, isSSOAuthModalOpen: true }))
                  } else {
                    setReauthenticationInfo((state) => ({ ...state, isAuthModalOpen: true }))
                  }
                  return
                }
                onOpenReviewPrescriptionModal()
              }}
            >
              Submit
            </Button>
          </VStack>

          {isReviewPrescriptionModalOpen && (
            <ReviewPrescriptionModal
              isOpen={isReviewPrescriptionModalOpen}
              isPaperPrescription={isPaperPrescription}
              isIncludeS8={!!checkIfSelectedDrugIncludesS8()}
              onClose={onCloseReviewPrescriptionModal}
              sendPrescription={() => {
                onOpenStepperModal()
              }}
            />
          )}

          {isStepperModalOpen && (
            <SendScriptOverallCheckModal
              isOpen={isStepperModalOpen}
              onClose={onCloseStepperModal}
              onSubmitPrescriptions={onSubmitPrescriptions}
              reauthenticationInfo={reauthenticationInfo}
              setReauthenticationInfo={setReauthenticationInfo}
            />
          )}

          {isScriptResultModalOpen && (
            <SendScriptResultsModal
              isOpen={isScriptResultModalOpen}
              onClose={handleCloseScriptResultModalByClose}
              onEditFailed={handleCloseScriptResultModalByEditFailed}
              states={getAllStates()}
              prescribedItems={prescribedItems}
              prescriptionsNumber={prescriptionsNumber}
              onAbort={abortCreatePrescriptionBatch}
              isLoading={isCreatePrescriptionBatchLoading()}
              prescriptions={validPrescriptions}
              onResendToken={setIsResendModelOpen}
            />
          )}

          {isResendModelOpen && (
            <ReIssuePrescriptionModal
              isOpen={isResendModelOpen}
              onClose={closeReIssueModal}
              prescriptions={availableResendPrescriptions}
            />
          )}
        </VStack>
        {reauthenticationInfo.isAuthModalOpen && (
          <AuthenticationModal
            isOpen={reauthenticationInfo.isAuthModalOpen}
            onClose={() =>
              setReauthenticationInfo((state) => ({ ...state, isAuthModalOpen: false }))
            }
            onSuccess={() =>
              setReauthenticationInfo({
                isDone: true,
                isAuthModalOpen: false,
                isSSOAuthModalOpen: false,
              })
            }
          />
        )}
        {reauthenticationInfo.isSSOAuthModalOpen && (
          <SSOAuthenticationModal
            isOpen={reauthenticationInfo.isSSOAuthModalOpen}
            onClose={() =>
              setReauthenticationInfo((state) => ({ ...state, isSSOAuthModalOpen: false }))
            }
            onSuccess={() =>
              setReauthenticationInfo({
                isDone: true,
                isAuthModalOpen: false,
                isSSOAuthModalOpen: false,
              })
            }
            onError={(error) =>
              setReauthenticationInfo((state) => ({
                ...state,
                isSSOAuthModalOpen: false,
                ssoError: error,
              }))
            }
          />
        )}

        {isPaperPrescriptionConfirmationModalOpen && (
          <AlertModel
            isOpen={isPaperPrescriptionConfirmationModalOpen}
            onClose={() => setIsPaperPrescriptionConfirmationModalOpen(false)}
            title="Prescription Type Change Confirmation"
            alertTitle="Are you sure you want to revert the prescription to paperless?"
            alertDescription="We will just keep the first PBS item you selected if you decide to make this prescription paperless."
            status="warning"
            actions={[
              {
                name: "Yes, Please untick the 'Create Paper Script' checkbox for me",
                action: handleUpdateIsPaperPrescriptionConfirmation,
              },
            ]}
          />
        )}
        <AlertModel
          isOpen={isAlertModelOpen}
          onClose={() => setIsAlertModelOpen(false)}
          title="Timeout"
          alertTitle="Request timed out"
          alertDescription="The server did not respond to the request within the allocated time. You can resend e-prescription or print a paper prescription by ticking the Create Paper Script checkbox."
          status="error"
          actions={[]}
        />
      </VStack>
    </Card>
  )
}

export default SendScriptPanel
